import { ChannelEvent, SignalConnectionState, SignalEvent } from '../signal/constant';
import EventEmitter from 'eventemitter3';
import {
  NAME,
  PEER_CONNECTION_STATE
} from 'trtc-js-sdk-core/src/common/constants';
import { isGetReceiversSupported } from 'trtc-js-sdk-core/src/common/rtc-detection';
import timer from 'trtc-js-sdk-core/src/utils/timer';
import { isNumber } from 'trtc-js-sdk-core/src/utils/utils';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import INNER_EVENT from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import loggerManager from 'trtc-js-sdk-core/src/utils/log/logger-manager';
import { ConnectionEvent, ConnectionState, Logger, NetworkQuality, NetworkQualityValue, TIMER_TYPE } from 'trtc-js-sdk-core';
import { Room } from '../room';
import SignalChannel from '../signal/signal-channel';
import { ping } from './ping';
import kvStatManager, { KV_REPORT_KEY_NETWORK } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';


const NETWORK_POOR_THRESHOLD = 700;

// function isMainlandChina() {
//   const isUTC8 = (0 - new Date().getTimezoneOffset() / 60) === 8;
//   if (!isUTC8) return false;

//   const { language } = navigator;
//   if (!language.includes('zh'))  return false;

//   if (language.includes('TW') || language.includes('HK') || language.includes('Hant')) return false;

//   return true;
// }

interface PingResult {
  domain: string;
  cost: number;
}

interface PingResults {
  isPoorNetwork?: boolean;
  timestamp?: number;
  data?: PingResult[];
}

/**
 * 网络质量检测模块
 *
 * 基于 rtt 和 loss 检测上下行的网络质量，并抛出 network-quality 事件
 * @class NetworkQuality
 */
export default class NetworkQualityManager extends EventEmitter implements NetworkQuality {
  private _room: Room;
  private _signalChannel: SignalChannel;
  private _log: Logger;
  uplinkRTT = 0;
  uplinkLoss = 0;
  // 所有下行连接的 RTT 平均值
  downlinkRTT = 0;
  // 所有下行连接的 Loss 平均值
  downlinkLoss = 0;
  // 弱网时 ping 第三方站点的结果
  pingResults: PingResults = {};
  // 通过两次 stat 之间的差值，算出当前统计周期的丢包数和收包数，进而算出丢包率（loss）
  private _downlinkPrevStatMap: Map<RTCPeerConnection, { totalPacketsLost: number, totalPacketsReceived: number; }> = new Map();  // Map<RTCPeerConnection, { totalPacketsLost, totalPacketsReceived }
  private _downlinkLossAndRTTMap: Map<RTCPeerConnection, { rtt: number, loss: number, userId: string; videoDelay?: number; audioDelay?: number; }> = new Map(); // Map<RTCPeerConnection, { rtt, loss, userId }>
  private _interval = -1;
  // 上行网络质量统计相关
  // 注意：由于使用了 setter 来检测网络质量变更，所以不要直接赋值 _uplinkNetworkQuality，而应该赋值 uplinkNetworkQuality
  private _uplinkNetworkQuality = NetworkQualityValue.UNKNOWN;
  private _downlinkNetworkQuality = NetworkQualityValue.UNKNOWN;
  // 网络质量历史记录
  private static readonly HISTORY_SIZE = 10;
  private _uplinkQualityHistory: number[] = [];
  private _downlinkQualityHistory: number[] = [];

  static EVENT_NETWORK_QUALITY = '0';

  static lastPingTime = 0;

  constructor({ signalChannel, room }: { signalChannel: SignalChannel; room: Room; }) {
    super();
    this._room = room;
    this._signalChannel = signalChannel;
    this._log = loggerManager.createLogger({
      parent: room.getLogger(),
      id: 'q',
      userId: this._room.userId,
      sdkAppId: this._room.sdkAppId
    });
    this.initialize();
  }

  get uplinkNetworkQuality() {
    return this._uplinkNetworkQuality;
  }

  set uplinkNetworkQuality(value) {
    if (value !== this._uplinkNetworkQuality) {
      // change
      this._log.info(`uplink ${this.uplinkNetworkQuality} -> ${value}, rtt: ${this.uplinkRTT}, loss: ${this.uplinkLoss} ws-rtt: ${this._signalChannel.rtt}`);
    }
    this._uplinkNetworkQuality = value;
    // 维护历史
    this._uplinkQualityHistory.push(value);
    if (this._uplinkQualityHistory.length > NetworkQualityManager.HISTORY_SIZE) {
      this._uplinkQualityHistory.shift();
    }
  }

  get downlinkNetworkQuality() {
    return this._downlinkNetworkQuality;
  }

  set downlinkNetworkQuality(value) {
    if (value !== this._downlinkNetworkQuality) {
      const { rtt, loss } = this.getAverageLossAndRTT([...this._downlinkLossAndRTTMap.values()]);
      // change
      this._log.info(`downlink ${this.downlinkNetworkQuality} -> ${value}, rtt: ${rtt}, loss: ${loss} ws-rtt: ${this._signalChannel.rtt}`);
    }
    this._downlinkNetworkQuality = value;
    // 维护历史
    this._downlinkQualityHistory.push(value);
    if (this._downlinkQualityHistory.length > NetworkQualityManager.HISTORY_SIZE) {
      this._downlinkQualityHistory.shift();
    }
  }

  /**
   * initialize
   * ref to https://www.w3.org/TR/webrtc-stats/#dom-rtcreceivedrtpstreamstats
   * @memberof NetworkQuality
   */
  initialize() {
    this._signalChannel.on(SignalEvent.UPLINK_NETWORK_STATS, (event: any) => {
      this.handleUplinkNetworkQuality(event);
    });

    // 信令通道断开时，重置上行网络质量
    this._signalChannel.on(
      ChannelEvent.CONNECTION_STATE_CHANGED,
      this.handleSignalConnectionStateChange.bind(this)
    );

    this.start();
  }

  /**
   * 计算上行连接网络质量
   * @param {Object} event
   * @memberof NetworkQuality
   */
  handleUplinkNetworkQuality(event: any) {
    // code === 0 代表获取数据成功
    if (event.data.code !== 0) return;
    const content = event.data.data;
    // 更新端到端延迟数据
    if (content.delay) {
      this.updateDelay(content.delay);
    }
    if (this._room.signalChannel && content.wsRtt) this._room.signalChannel.rtt = content.wsRtt;
    if (!this._room.uplinkConnection) {
      this.uplinkNetworkQuality = NetworkQualityValue.UNKNOWN;
      this.uplinkLoss = 0;
      this.uplinkRTT = 0;
      return;
    }
    const uplinkPeerConnection = this._room?.uplinkConnection?.getPeerConnection();
    // 若 uplinkPeerConnection 断开，但信令通道依然连通，此时后台依然会下发 cmd: 22 信令；这种情况就没必要继续计算了，直接重置为断开即可
    if (uplinkPeerConnection && this.isPeerConnectionDisconnected(uplinkPeerConnection)) {
      this.uplinkNetworkQuality = NetworkQualityValue.DISCONNECTED;
      this.uplinkLoss = 0;
      this.uplinkRTT = 0;
      return;
    }

    const sentPackets = content.expectAudPkg + content.expectVidPkg;
    const receivedPackets = content.recvAudPkg + content.recvVidPkg;
    const packetsLost = sentPackets - receivedPackets;

    // 弱网条件下，信令回包的数据可能出现全为 0 的情况，此时使用上一次的网络质量数据
    if (sentPackets === 0 && receivedPackets === 0) {
      return;
    }
    if (packetsLost <= 0) {
      this.uplinkLoss = 0;
    } else {
      this.uplinkLoss = Math.round((packetsLost / sentPackets) * 100);
    }
    this.uplinkRTT = content.rtt;
    this.uplinkNetworkQuality = this.getNetworkQuality(this.uplinkLoss, this.uplinkRTT);
  }

  /**
   * 计算下行网络质量
   * @returns
   * @memberof NetworkQuality
   */
  async handleDownlinkNetworkQuality() {
    // 没有下行连接时，说明当前网络状态未知
    if (this._room.remotePublishedUserMap.size === 0) {
      this.downlinkNetworkQuality = NetworkQualityValue.UNKNOWN;
      return;
    }

    const connections = [...this._room.remotePublishedUserMap.values()];
    // 根据 peerConnection 去重，多个 connection 可能共用同一个 peerConnection
    const peerConnectionSet = new Set<RTCPeerConnection>();
    const uniqueConnections = connections.filter(connection => {
      const peerConnection = connection.getPeerConnection();
      if (!peerConnection || peerConnectionSet.has(peerConnection)) {
        return false;
      }
      peerConnectionSet.add(peerConnection);
      return true;
    });

    const connectedConnections = uniqueConnections.filter(connection => connection.getPeerConnection()?.connectionState === PEER_CONNECTION_STATE.CONNECTED);
    const disconnectedConnections = uniqueConnections.filter(connection => this.isPeerConnectionDisconnected(connection.getPeerConnection()));

    if (disconnectedConnections.length === connections.length) {
      this.downlinkNetworkQuality = NetworkQualityValue.DISCONNECTED;
      return;
    }

    // 统计每个下行连接的 rtt loss
    for (let i = 0; i < connectedConnections.length; i++) {
      const peerConnection = connectedConnections[i].getPeerConnection();
      if (!peerConnection) return;
      const { rtt, totalPacketsLost, totalPacketsReceived } = await this.getStat(peerConnection);

      if (!this._downlinkPrevStatMap.has(peerConnection)) {
        this._downlinkPrevStatMap.set(peerConnection, {
          totalPacketsLost,
          totalPacketsReceived
        });
        continue;
      }

      let loss = 0;
      const prevStat = this._downlinkPrevStatMap.get(peerConnection);
      const packetsLost = totalPacketsLost - prevStat!.totalPacketsLost;
      const packetsReceived = totalPacketsReceived - prevStat!.totalPacketsReceived;

      // 当前统计周期的丢包数可能会小于零，猜测是当前统计周期收到了上一统计周期丢弃的包，totalPacketsLost 比 prevStat.totalPacketsLost 小
      if (packetsLost <= 0 || packetsReceived < 0) {
        loss = 0;
      } else {
        loss = Math.round((packetsLost / (packetsLost + packetsReceived)) * 100);
      }

      this._downlinkPrevStatMap.set(peerConnection, {
        totalPacketsLost,
        totalPacketsReceived
      });

      this._downlinkLossAndRTTMap.set(peerConnection, {
        rtt,
        loss,
        userId: connectedConnections[i].getUserId(),
        audioDelay: connectedConnections[i].remoteAudioTrack.stat.end2EndDelay,
        videoDelay: connectedConnections[i].remoteVideoTrack.stat.end2EndDelay
      });
    }

    // 已断开的下行连接统计数据要清理掉
    [...this._downlinkPrevStatMap.keys()].forEach(peerConnection => {
      if (this.isPeerConnectionDisconnected(peerConnection)) {
        this._downlinkPrevStatMap.delete(peerConnection);
        this._downlinkLossAndRTTMap.delete(peerConnection);
      }
    });

    if (this._downlinkLossAndRTTMap.size === 0) {
      this.downlinkRTT = 0;
      this.downlinkLoss = 0;
      this.downlinkNetworkQuality = NetworkQualityValue.UNKNOWN;
      return;
    }

    const { rtt, loss } = this.getAverageLossAndRTT([...this._downlinkLossAndRTTMap.values()]);
    this.downlinkRTT = rtt;
    this.downlinkLoss = loss;
    this.downlinkNetworkQuality = this.getNetworkQuality(loss, rtt);
  }

  /**
   * 根据 peerConnection 获取下行网络质量数据
   * @param {RTCPeerConnection} peerConnection
   * @returns {Object}
   * @memberof NetworkQuality
   */
  async getStat(peerConnection: any) {
    const result = {
      rtt: 0,
      totalPacketsLost: 0,
      totalPacketsReceived: 0
    };

    if (!peerConnection || !isGetReceiversSupported()) {
      return result;
    }

    const receivers = peerConnection.getReceivers();

    try {
      for (let i = 0; i < receivers.length; i++) {
        const receiver = receivers[i];
        const stats = await receiver.getStats();
        stats.forEach((stat: any) => {
          if (stat.type === 'candidate-pair' && isNumber(stat.currentRoundTripTime)) {
            // 计算 rtt
            result.rtt = Math.round(stat.currentRoundTripTime * 1000);
          }

          // 计算丢包率
          if (
            stat.type === 'inbound-rtp' &&
            ((stat.mediaType || stat.kind) === NAME.AUDIO || (stat.mediaType || stat.kind) === NAME.VIDEO)
          ) {
            result.totalPacketsLost += stat.packetsLost; // 总丢包数
            result.totalPacketsReceived += stat.packetsReceived; // 总收包数
          }
        });
      }
      // Firefox 不支持从 candidate 获取 rtt，此处用后台计算的 uplinkRTT 兜底。
      // https://bugzilla.mozilla.org/show_bug.cgi?id=1761334
      if (result.rtt === 0) {
        result.rtt = this.uplinkRTT;
      }
      return result;
    } catch (error) {
      return result;
    }
  }

  /**
   * 取 stats 中 rtt loss 的平均值
   * @param {Object[]} stats
   * @returns
   * @example
   * const stats = [{ rtt:4, loss: 8 }, { rtt:8, loss:6 }];
   * const averageStat = getAverageStat(stats);
   * // { rtt: 6, loss: 7 }
   * @memberof NetworkQuality
   */
  getAverageLossAndRTT(stats: any) {
    const result = {
      rtt: 0,
      loss: 0
    };
    if (Array.isArray(stats) && stats.length > 0) {
      stats.forEach(stat => {
        result.rtt += stat.rtt;
        result.loss += stat.loss;
      });

      Object.keys(result).forEach(key => {
        // @ts-ignore
        result[key] = Math.round(result[key] / stats.length);
      });
    }
    return result;
  }

  /**
   * 根据丢包率和 RTT 计算网络质量
   * @param {Number} loss 丢包率
   * @param {Number} rtt
   * @returns {0|1|2|3|4|5|6}
   * @memberof NetworkQuality
   */
  getNetworkQuality(loss: number, rtt: number) {
    if (loss > 50 || rtt > 500) {
      return NetworkQualityValue.VERY_BAD;
    }
    if (loss > 30 || rtt > 350) {
      return NetworkQualityValue.BAD;
    }
    if (loss > 20 || rtt > 200) {
      return NetworkQualityValue.POOR;
    }
    if (loss > 10 || rtt > 100) {
      return NetworkQualityValue.GOOD;
    }
    if (loss >= 0 || rtt >= 0) {
      return NetworkQualityValue.EXCELLENT;
    }

    return NetworkQualityValue.UNKNOWN;
  }

  /**
   * 信令通道断开时，重置上行网络质量数据
   * @param {*} event
   * @memberof NetworkQuality
   */
  handleSignalConnectionStateChange(event: any) {
    if (event.state === SignalConnectionState.DISCONNECTED) {
      this.uplinkRTT = 0;
      this.uplinkLoss = 0;
      this.uplinkNetworkQuality = NetworkQualityValue.DISCONNECTED;
    } else if (
      event.state === SignalConnectionState.CONNECTED &&
      this.uplinkNetworkQuality === NetworkQualityValue.DISCONNECTED
    ) {
      // 可能因网络原因，导致上行网络质量信令下发有延迟。disconnected -> connected 后，将上行网络质量改为 1
      // 等后续网络质量信令下发后，再更新实际的网络质量。
      this.uplinkNetworkQuality = NetworkQualityValue.EXCELLENT;
    }
  }

  /**
   * 上行连接断开时，重置上行网络质量数据
   * @param {*} event
   * @memberof NetworkQuality
   */
  handleUplinkConnectionStateChange({ state }: { state: ConnectionState; }) {
    if (state === ConnectionState.DISCONNECTED) {
      this.uplinkLoss = 0;
      this.uplinkRTT = 0;
      this.uplinkNetworkQuality = NetworkQualityValue.DISCONNECTED;
    } else if (
      state === ConnectionState.CONNECTED &&
      this.uplinkNetworkQuality === NetworkQualityValue.DISCONNECTED
    ) {
      // 可能因网络原因，导致上行网络质量信令下发有延迟。disconnected -> connected 后，将上行网络质量改为 5
      // 等后续网络质量信令下发后，再更新实际的网络质量。
      this.uplinkNetworkQuality = NetworkQualityValue.VERY_BAD;
    }
  }

  isPeerConnectionDisconnected(peerConnection: RTCPeerConnection | null) {
    if (
      peerConnection &&
      (peerConnection.connectionState === PEER_CONNECTION_STATE.DISCONNECTED ||
        peerConnection.connectionState === PEER_CONNECTION_STATE.FAILED ||
        peerConnection.connectionState === PEER_CONNECTION_STATE.CLOSED)
    ) {
      return true;
    }
    return false;
  }

  setUplinkConnection(uplinkConnection: any) {
    this._room.uplinkConnection = uplinkConnection;
    if (this._room.uplinkConnection) {
      this._room.uplinkConnection.on(
        ConnectionEvent.CONNECTION_STATE_CHANGED,
        this.handleUplinkConnectionStateChange.bind(this)
      );
    } else {
      this.uplinkNetworkQuality = NetworkQualityValue.UNKNOWN;
      this.uplinkRTT = 0;
      this.uplinkLoss = 0;
    }
  }

  start() {
    if (this._interval !== -1) {
      this._log.info('network quality calculating is already started');
      return;
    }
    this._log.debug('start network quality calculating');
    this._interval = timer.run(TIMER_TYPE.RIC, () => {
      this.handleDownlinkNetworkQuality();
      const downlinks = [...this._downlinkLossAndRTTMap.values()];
      innerEmitter.emit(INNER_EVENT.NETWORK_QUALITY, {
        room: this._room,
        uplink: {
          rtt: this.uplinkRTT,
          loss: this.uplinkLoss
        },
        downlinks
      });

      const pingDomainInfo = this._room.scheduleResult.config?.pingDomainInfo;

      let networkQuality: any = {
        uplinkNetworkQuality: this.uplinkNetworkQuality,
        downlinkNetworkQuality: this.downlinkNetworkQuality,
        uplinkRTT: this.uplinkRTT,
        uplinkLoss: this.uplinkLoss,
        downlinkRTT: this.downlinkRTT,
        downlinkLoss: this.downlinkLoss
      };
      if (pingDomainInfo) {
        networkQuality = {
          ...networkQuality,
          pingResults: (this.uplinkRTT > pingDomainInfo.rttThreshold || this.downlinkRTT > pingDomainInfo.rttThreshold) ? this.pingResults : {}
        };
      }
      this.emit(NetworkQualityManager.EVENT_NETWORK_QUALITY, networkQuality);

      const currentTime = Date.now();

      if (pingDomainInfo &&
        (this.uplinkRTT > pingDomainInfo.rttThreshold || this.downlinkRTT > pingDomainInfo.rttThreshold) && // rtt 大于云控下发的阈值开始 ping
        currentTime - NetworkQualityManager.lastPingTime > pingDomainInfo.interval * 1000 // 云控控制多久 ping 一次
      ) {
        NetworkQualityManager.lastPingTime = Date.now();
        const pingPromises = pingDomainInfo.domain.map(value => ping(value).then(result => ({
          domain: value,
          cost: result.totalCost
        })));
        Promise.all(pingPromises).then(results => {
          this.pingResults.isPoorNetwork = results.some(result => result.cost > NETWORK_POOR_THRESHOLD);
          this.pingResults.timestamp = currentTime;
          this.pingResults.data = results;
          results.forEach(result => {
            kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.PING, cost: result.cost });
          });
          this._log.warn(`All ping results: ${JSON.stringify(results)}`);
        })
          .catch(error => {
            this._log.warn(`Error during pinging domains: ${error}`);
          });
      }
    }, { delay: 2000 });
  }

  /** 近期是否出现过差的上行网络 */
  hadRecentBadUplink(threshold = 2): boolean {
    return this._uplinkQualityHistory.some(q => q > threshold);
  }

  /** 近期是否出现过差的下行网络 */
  hadRecentBadDownlink(threshold = 2): boolean {
    return this._downlinkQualityHistory.some(q => q > threshold);
  }

  stop() {
    this._log.debug('stopped');
    if (this._interval !== -1) {
      timer.clearTask(this._interval);
      this._interval = -1;
    }
    this._downlinkLossAndRTTMap.clear();
    this._downlinkPrevStatMap.clear();
  }

  /**
   * update delay in downlink connections
   * @param {Array} delays
   * @memberof NetworkQuality
   */
  updateDelay(delays: { srcTinyId: any; videoDelay: any; audioDelay: any; }[]) {
    const { tinyIdToUserIdMap } = this._room;
    delays.forEach(({ srcTinyId, videoDelay, audioDelay }) => {
      const userId = tinyIdToUserIdMap.get(srcTinyId);
      if (userId) {
        const downlinkConnection = this._room.remotePublishedUserMap.get(userId);
        downlinkConnection?.setDelay({ videoDelay, audioDelay });
      }
    });
  }
}
